import {AfterViewInit, Component, EventEmitter, Input, Output} from "@angular/core";
import {ErrorHandlerService} from "../../../error-handler.service";
import {BillingAccountService} from "./billing-account.service";
import {BillingSetupIntentData} from "./billing-setup-intent.data";
import {FUIAlertService, FUISpinnersService} from "@fedelta-pos/ui";

@Component({
    selector: "fhm-billing-direct-debit-form",
    styles: [":host {display: flex; width: 100%;}"],
    templateUrl: "./billing-direct-debit-form.component.html",
    styleUrls: ["./billing-direct-debit-form.component.scss"],
    providers: [BillingAccountService],
    standalone: false
})
export class BillingDirectDebitFormComponent implements AfterViewInit {

    @Input() venueId: string;
    @Output() confirmBilling: EventEmitter<any> = new EventEmitter<any>();

    billingName: string;
    billingEmail: string;

    bankName: string;

    stripe: any;
    auBankAccountElement: any;
    clientSecret: string;

    constructor(private billingAccountService: BillingAccountService,
                private errorHandler: ErrorHandlerService,
                private alertService: FUIAlertService,
                private spinnersService: FUISpinnersService) {
    }

    ngAfterViewInit(): void {
        this.setupStripe();
    }

    async setupStripe(): Promise<void> {
        let billingSetupIntent: BillingSetupIntentData;
        this.spinnersService.open();
        try {
            billingSetupIntent = await this.billingAccountService.createSetupIntent(this.venueId);
        } finally {
            this.spinnersService.close();
        }

        this.stripe = new (<any>window).Stripe(billingSetupIntent.stripePublicKey);
        this.clientSecret = billingSetupIntent.clientSecret;
        this.billingName = billingSetupIntent.firstName + " " + billingSetupIntent.lastName;
        this.billingEmail = billingSetupIntent.email;

        const elements = this.stripe.elements();
        const style = {
            base: {
                color: "#32325d",
                fontSize: "16px",
                "::placeholder": {
                    color: "#aab7c4"
                },
                ":-webkit-autofill": {
                    color: "#32325d",
                },
            },
            invalid: {
                color: "#fa755a",
                iconColor: "#fa755a",
                ":-webkit-autofill": {
                    color: "#fa755a",
                },
            }
        };

        const options = {
            style: style,
            disabled: false,
            hideIcon: false,
            iconStyle: "default" // or "solid"
        };
        this.auBankAccountElement = elements.create("auBankAccount", options);
        this.auBankAccountElement.mount("#au-bank-account-element");

        this.auBankAccountElement.on("change", (event) => {
            // Reset error state
            document.getElementById("error-message").classList.remove("visible");
            // Display bank name corresponding to auBankAccount, if available.
            const bankName = document.getElementById("bank-name");
            if (event.bankName && event.branchName) {
                bankName.textContent = `${event.bankName} (${event.branchName})`;
                bankName.classList.add("visible");
            } else if (event.bankName) {
                bankName.textContent = `${event.bankName}`;
                bankName.classList.add("visible");
            } else {
                bankName.classList.remove("visible");
            }
            // Handle real-time validation errors from the Element.
            if (event.error) {
                this.showError(event.error.message);
            }
        });
    }

    showError(errorMsgText: string): void {
        const errorMsg = document.querySelector("#error-message");
        errorMsg.textContent = errorMsgText;
        errorMsg.classList.add("visible");
    }

    async save(): Promise<void> {
        this.spinnersService.open();
        let result;
        try {
            result = await this.stripe
                .confirmAuBecsDebitSetup(this.clientSecret, {
                    payment_method: {
                        au_becs_debit: this.auBankAccountElement,
                        billing_details: {
                            name: this.billingName,
                            email: this.billingEmail
                        }
                    }
                });
        } finally {
            this.spinnersService.close();
        }
        if (result.setupIntent) {
            this.confirmBilling.emit(result.setupIntent.payment_method);
        } else if (result.error) {
            this.alertService.showError("Direct debit setup failed", result.error.message).subscribe(() => {
            });
        }
        console.log(result);
    }
}
